"use client";

import * as React from "react";
import {
  X,
  FileText,
  BarChart3,
  ScanSearch,
  Link2,
  ExternalLink,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import { cn } from "../lib/cn";
import { Button } from "../primitives/Button";
import { AnimatedExperienceTypeIcon } from "./AnimatedExperienceTypeIcon";
import { getExperienceTypeMeta, type ExperienceTypeTone } from "./ExperienceTypeBadge";
import { StatusChip, type StatusExpressionState } from "./StatusExpression";

/**
 * ArtifactFocus — the drill-in detail view for one Brain artifact.
 *
 * When a creator clicks a card in the Brain/session sidebar, the panel focuses
 * that one item: a dark hero stage that names it, a live Preview of the artifact
 * body, its Details, its Sources, and type-aware Actions whose primary CTA is
 * "Open draft" / "Open live ↗". It is the expanded counterpart to `ArtifactTile`
 * — same panel, no layout jump.
 *
 * Presentational only. It consumes one already-mapped `ArtifactFocusItem`
 * (the host maps a unified#103 `/items/` item into this view-model) and emits
 * `onAction` / `onClose` intents — it never fetches, owns no router/breadcrumb,
 * and never imports the AIStudio-v3 renderers (those arrive via the `preview`
 * slot, keeping `@aisquare/ui` dependency-clean). For non-experience kinds with
 * no v3 renderer it falls back to a lightweight per-kind preview stand-in.
 *
 * Tokens: authored against the library's semantic tokens (`bg-primary` hero,
 * `bg-card`, `text-accent`, ...) — NOT the design mock's `--hero`/`--plum`/`--ac`
 * names, which do not exist in the lib. Per-type identity color is derived the
 * lib way from `getExperienceTypeMeta` (identity-only: the icon tile + label
 * dot), not a raw `accentVar` string. Theme-aware (light + `.dark`),
 * reduced-motion safe (transform-only entrances; the hero aurora loop degrades
 * to a static glow under the preset's reduced-motion guard).
 *
 * Layout is a flex column of full-width rows: hero, preview, details+sources,
 * status, actions. Opt-in props let a host reshape it without a CSS override:
 *   - `stageVariant="bare"` un-cards the preview and makes it the sole
 *     full-height, scroll-owning row (the host gives the component a height).
 *   - `stageVariant="flush"` keeps the preview CARDED (same border, radius,
 *     background and shadow as the default) but drops the "PREVIEW" eyebrow and
 *     the cell's own padding, so the stage sits flush against the card edge and
 *     clips to its radius. Unlike `bare` it stays an ordinary row, so it
 *     composes with `belowPreview` and `actionsPlacement` instead of claiming
 *     the column's height.
 *   - `stageVariant="detached"` renders NO preview row at all — chrome only,
 *     sized to content, for a host that renders the real stage as its own
 *     sibling region (the uni#355 "chrome up top, stage below" shape without
 *     the DOM-hiding bridge it needed before this variant existed).
 *   - `actions` swaps a host-owned actions row in for the type-aware default.
 *   - the PQA#2615 layout set (`headerMedia`, `headerExtra`,
 *     `actionsPlacement="belowHero"`, `belowPreview`) reshapes the card to the
 *     owner's preview spec: full-height cover in the header, meta on the
 *     header's right, status + actions directly below the header, and a host
 *     slot between the preview and the Details row.
 * All default to the original behaviour, so existing consumers are
 * unchanged. Grounding-source chips accept an optional per-chip `onClick`
 * (see `ArtifactFocusSourceChip`) that promotes that chip — and only that
 * chip — to a real button; plain-string chips render exactly as before.
 */

export type ArtifactFocusKind = "experience" | "file" | "chart" | "analysis" | "link" | "connector";

/**
 * One grounding-source chip. A plain string stays a static chip (the original
 * shape, unchanged); give the object form an `onClick` to make that chip a
 * real, keyboard-operable button. Interactivity is per-chip so a source with
 * nothing to open is never a dead button — the host attaches the intent
 * directly to the chips that have one, with no index bookkeeping.
 */
export interface ArtifactFocusSourceChip {
  /** Chip text (also the hover title). */
  label: string;
  /** Present = this chip is a button firing the host's open-source intent. */
  onClick?: () => void;
}

export interface ArtifactFocusItem {
  id: string;
  title: string;
  /** Short type label, e.g. "Quiz" / "Note" / "PDF". */
  label: string;
  /** Provenance subtitle, e.g. "Generated by AIKO" / "Uploaded · indexed". */
  subtitle?: string;
  /** Summary pill copy, e.g. "MCQ · 10 questions". */
  summary?: string;
  kind: ArtifactFocusKind;
  /** When `kind === "experience"`: the experience type, driving icon + identity color. */
  expType?: "quest" | "note" | "podcast" | "video" | "expert";
  /** Grounded-on-sources shield. */
  grounded?: boolean;
  /** Shield copy, e.g. "3 sources". */
  groundLabel?: string;
  /**
   * Cover art for the artifact, rendered IN THE HEADER as a FULL-HEIGHT media
   * panel beside the identity (PQA#2615 round 2: the owner called the first
   * pass's small thumb "too small" — the cover now spans the hero's height,
   * inset by the hero's padding, with the same rounded radius as the Preview
   * cell so the two read as one system). Omit for a cover-less artifact and
   * the header keeps its text-only layout.
   */
  cover?: string;
  /**
   * Tags, rendered on the RIGHT side of the header (PQA#2615: the header's
   * empty right space becomes useful), opposite the identity, rather than down
   * in the details table. Empty / omitted renders no row at all.
   */
  tags?: string[];
  /** Details rows: `[key, value]`. */
  facts: Array<[string, string]>;
  /** Sources cell: a dynamic label + chips (strings, or `ArtifactFocusSourceChip`
      objects when a chip should be clickable). */
  sources: { label: string; chips: Array<string | ArtifactFocusSourceChip> };
  /** unified#103 lifecycle status. */
  status: StatusExpressionState;
  /** Live URL, `null` while draft/generating — drives the primary CTA label. */
  liveUrl: string | null;
  /** How this item entered the session. */
  relation: "created" | "uploaded" | "linked" | "published";
}

export interface ArtifactFocusProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  item: ArtifactFocusItem;
  /**
   * The real artifact body (for experiences, the AIStudio-v3 CenterStage stage,
   * built + passed by the host). When omitted, the lib renders its lightweight
   * per-kind stand-in for file / chart / analysis / link.
   */
  preview?: React.ReactNode;
  /**
   * Preview stage presentation.
   * - `"carded"` (default): the preview sits in a labelled `bg-card` box that
   *   sizes to its content — the original 1.9 layout, unchanged.
   * - `"bare"`: the preview stage renders un-carded (no border / background /
   *   "Preview" label) and full-height — it becomes the sole `flex-1`,
   *   `min-h-0`, scroll-owning row, so a host can drop a full-height renderer
   *   straight in without the card clipping or collapsing it. The host must give
   *   the component a bounded height (in this variant it applies `h-full
   *   min-h-0` to itself). The Onyx hero notch and the details / sources /
   *   status rows are unchanged.
   * - `"detached"`: NO preview row renders — the component is identity chrome
   *   only (hero, details + sources side by side, status, actions), sized to
   *   its content. For hosts that render the real stage as a sibling region
   *   below the chrome and previously had to hide the empty bare row from
   *   outside. The host owns the stage's height and scroll entirely.
   */
  stageVariant?: "carded" | "bare" | "detached" | "flush";
  /**
   * Host-supplied actions row, rendered in place of the library's type-aware
   * default (Edit / Settings / Open-draft, ...). When provided, the default row
   * is not rendered and its `onAction` is not called — the host owns the buttons
   * and their intents. Omit to keep the default behaviour.
   */
  actions?: React.ReactNode;
  /**
   * Host-owned hero media panel (PQA#2615): rendered as the header's
   * full-height media column IN PLACE of the static `item.cover` image, so a
   * host can put its live cover component — with its upload / AI-generate
   * dropdown controls — directly in the header. The slot only supplies the
   * box (full-height, Preview-cell radius, hero-inset padding); the content,
   * its states, and its behaviour are the host's. NB the slot box is NOT
   * overflow-clipped (a dropdown menu inside it must be able to escape) — the
   * host's content clips its own corners to the panel's 13px radius.
   */
  headerMedia?: React.ReactNode;
  /**
   * Extra header meta for the right-side column, rendered under the tags
   * (PQA#2615: e.g. the published experience's studio hint / name / hover
   * preview). Omit and the right column holds tags alone; with neither, the
   * header keeps its single-column identity layout.
   */
  headerExtra?: React.ReactNode;
  /**
   * Where the status + actions block renders (PQA#2615: "the buttons need to
   * be right below the header so that action is easy and upfront").
   * - `"end"` (default): the original order — status row then actions row
   *   after Details/Sources. Existing consumers are byte-unchanged.
   * - `"belowHero"`: ONE compact row (StatusChip + the actions) directly
   *   below the hero, before the preview.
   */
  actionsPlacement?: "end" | "belowHero";
  /**
   * Rendered between the preview row and the Details/Sources row (PQA#2615:
   * "tags need to be right below the preview and right above the details
   * stuff" — the host's EDITABLE tags block lands here; the header's `tags`
   * stay the read-at-a-glance chips). Omitted renders nothing.
   */
  belowPreview?: React.ReactNode;
  onAction?: (action: string) => void;
  onClose?: () => void;
}

/** Per-type identity tint, keyed by the lib `getExperienceTypeMeta` tone — color
 *  lives ONLY in the hero icon tile + the label dot (identity-only). */
const TONE_ACCENT: Record<ExperienceTypeTone, { dot: string; text: string; tile: string }> = {
  emerald: {
    dot: "bg-emerald-400",
    text: "text-emerald-300",
    tile: "bg-emerald-500/20 text-emerald-200",
  },
  sky: { dot: "bg-sky-400", text: "text-sky-300", tile: "bg-sky-500/20 text-sky-200" },
  cyan: { dot: "bg-cyan-400", text: "text-cyan-300", tile: "bg-cyan-500/20 text-cyan-200" },
  violet: {
    dot: "bg-violet-400",
    text: "text-violet-300",
    tile: "bg-violet-500/20 text-violet-200",
  },
  amber: { dot: "bg-amber-400", text: "text-amber-300", tile: "bg-amber-500/20 text-amber-200" },
  rose: { dot: "bg-rose-400", text: "text-rose-300", tile: "bg-rose-500/20 text-rose-200" },
  slate: {
    dot: "bg-white/60",
    text: "text-primary-foreground/80",
    tile: "bg-white/12 text-primary-foreground",
  },
  neutral: {
    dot: "bg-white/60",
    text: "text-primary-foreground/80",
    tile: "bg-white/12 text-primary-foreground",
  },
};

const KIND_ICON: Record<Exclude<ArtifactFocusKind, "experience">, LucideIcon> = {
  file: FileText,
  chart: BarChart3,
  analysis: ScanSearch,
  link: Link2,
  connector: ExternalLink,
};

/** Type-aware action sets. `*` (last item) is the primary CTA. */
const ACTIONS: Record<ArtifactFocusKind, string[]> = {
  experience: ["Edit", "Settings", "Open"],
  file: ["Open", "Download"],
  chart: ["Export", "Download"],
  analysis: ["Export", "Open"],
  link: ["Copy link", "Open"],
  connector: ["Re-sync", "Open"],
};

/**
 * The width the host panel should grow to on drill-in (grow-only: the host does
 * `Math.max(currentWidth, recommendedFocusWidth(item))`). Podcast/video need the
 * most room, experiences a bit, everything else the least.
 *
 * A TOTAL-room hint, not a two-column promise. The details/sources row carries a
 * 240px cell floor of its own, so below ~490 (the 470 panel, for instance) that
 * row stacks: one full-width cell reads a long filename, two ~175px ones clip
 * it. The numbers answer different questions and are not meant to agree.
 */
export function recommendedFocusWidth(item: ArtifactFocusItem): number {
  if (item.kind === "experience" && (item.expType === "podcast" || item.expType === "video")) {
    return 580;
  }
  if (item.kind === "experience") return 520;
  return 470;
}

/** The primary CTA label: draft until a live URL exists, then "Open live ↗". */
function primaryActionLabel(item: ArtifactFocusItem): string {
  if (item.kind !== "experience") return ACTIONS[item.kind][ACTIONS[item.kind].length - 1];
  return item.liveUrl ? "Open live ↗" : "Open draft";
}

const MONO_LABEL = "font-mono text-[9px] uppercase tracking-[0.13em] text-muted-foreground";

/* ---------------------------------------------------------------- Hero ---- */

function HeroStage({
  item,
  onClose,
  media,
  extra,
}: {
  item: ArtifactFocusItem;
  onClose?: () => void;
  media?: React.ReactNode;
  extra?: React.ReactNode;
}) {
  const typeMeta =
    item.kind === "experience" ? getExperienceTypeMeta(item.expType ?? "experience") : null;
  const accent = typeMeta ? TONE_ACCENT[typeMeta.tone] : TONE_ACCENT.slate;
  const hasMedia = media != null || !!item.cover;
  // The right-hand meta column is the OPT-IN PQA#2615 layout, and the host opts
  // in by passing `headerExtra` — NOT by the artifact happening to carry tags.
  // That keeps the two tickets on opposite sides of one seam: #2645 wants tags
  // in the naming column under the title (the default every consumer gets),
  // #2615 wants them on the header's right beside the host's studio hint (the
  // opt-in). Deriving this from `extra` rather than from `item.tags` is what
  // stops the opt-in layout leaking onto the default path.
  const hasMeta = extra != null;
  const tags = item.tags && item.tags.length > 0 ? item.tags : null;

  return (
    <div
      data-slot="hero"
      // NOT overflow-clipped: a host dropdown inside the media slot (the AI-cover
      // "Generate" menu) must be able to escape the hero. The aurora layer below
      // carries its own rounded clip instead, so the glow still honours the
      // hero's corners.
      className="relative col-span-2 shrink-0 rounded-[14px] border border-primary/40 bg-primary px-[17px] py-4 text-primary-foreground shadow-lift"
    >
      {/* ambient aurora glow — decorative, degrades to a static glow under reduced motion */}
      <span
        aria-hidden
        className="animate-aurora-drift pointer-events-none absolute inset-0 -z-0 overflow-hidden rounded-[14px] bg-[radial-gradient(120%_110%_at_86%_-14%,hsl(var(--accent)/0.45),transparent_60%)] bg-[length:180%_180%]"
      />
      <div className={cn("relative z-10", hasMedia && "flex items-stretch gap-3.5")}>
        {/* PQA#2615 round 2: the cover is the header's FULL-HEIGHT media column —
            it stretches to the identity column's height (min-h floors a sparse
            hero so the art still reads), inset by the hero's own padding, on the
            Preview cell's 13px radius so header and preview read as one system.
            `headerMedia` swaps the host's live cover component (with its upload /
            AI-generate controls) into the same box; the static `item.cover` form
            stays for view-only hosts. A cover that fails to load hides the whole
            panel, leaving the text layout intact instead of an empty frame. */}
        {hasMedia && (
          <div
            data-slot="header-media"
            className={cn(
              // self-stretch pairs with max-h so the panel fills the header's
              // height but a degenerate identity column (a very long title in a
              // very narrow host) can never stretch it into a tower.
              "min-h-[104px] max-h-[170px] w-[34%] min-w-[96px] max-w-[220px] shrink-0 self-stretch rounded-[13px] border border-white/20 bg-white/5 shadow-sm",
              // The static img form clips its own corners here; the SLOT form does
              // not get overflow-hidden, so host controls inside it (an AI-style
              // dropdown menu) can escape the panel — the host owns its content's
              // corner clipping instead (documented on `headerMedia`).
              media == null && "overflow-hidden",
            )}
          >
            {media ?? (
              <img
                src={item.cover}
                alt=""
                /* Decorative: the title beside it already names the artifact, so an
                   alt would just repeat it to a screen reader. */
                className="h-full w-full object-cover"
                onError={(e) => {
                  const panel = e.currentTarget.closest<HTMLElement>('[data-slot="header-media"]');
                  if (panel) panel.style.display = "none";
                }}
              />
            )}
          </div>
        )}
        {/* Compact identity BAND (PQA#2645, owner 2026-07-22 + design research):
            ONE avatar-style row — leading icon, the naming column, then close on
            the trailing edge — instead of the icon sitting on its own line above
            the name. Removing that standalone row and folding the state chips
            (summary / grounded) up onto the label's line turns the header into a
            proper band: a wide-panel drag reads as a header bar (Jakob's-law
            identity headers — GitHub / Linear / Notion / Stripe are all compact,
            left-aligned, minimal-height) rather than a tall, mostly-empty navy
            field, and the horizontal space carries real content instead of dead
            gradient. Everything degrades: no subtitle / summary / grounded /
            cover / tags simply collapses each slot. */}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            className={cn(
              "inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg",
              accent.tile,
            )}
          >
            {item.kind === "experience" ? (
              <AnimatedExperienceTypeIcon
                type={item.expType ?? ""}
                active={false}
                size="sm"
                fallback={React.createElement(typeMeta!.Icon, {
                  className: "h-4 w-4",
                  "aria-hidden": true,
                })}
              />
            ) : (
              // `item.kind` is narrowed to a non-experience kind here.
              React.createElement(KIND_ICON[item.kind], {
                className: "h-4 w-4",
                "aria-hidden": true,
              })
            )}
          </span>
          {/* PQA#2615 opt-in: identity on the left, tags + host meta (the published
              studio hint) on the RIGHT — the header's empty right space carries the
              taxonomy instead of the details table. The row WRAPS: in a narrow host
              column (the resizable context bar's floor) the meta drops onto its own
              right-aligned line below the identity instead of clamping the title or
              colliding with the label pill. Without `headerExtra` this collapses to
              the default single naming column. */}
          <div
            className={cn(
              "min-w-0 flex-1",
              hasMeta && "flex flex-wrap items-start justify-between gap-x-3",
            )}
          >
            <div className="min-w-0 flex-1 basis-40">
              {/* Meta row: the label + state chips are ONE left-aligned group
                  (Gestalt proximity — they read as a single band of card metadata).
                  They are NOT pushed to the trailing edge: an ml-auto trailing group
                  looks balanced at wide width but, when the panel is dragged to its
                  narrow floor, the chip wraps to a line of its own and pins itself
                  right, stranded in empty space. Left-aligned, a wrapped chip drops
                  cleanly under the label instead. Only SHORT tokens live here; the
                  long provenance subtitle is a separate line below. */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/12 px-2.5 py-1 text-[11px] font-medium text-primary-foreground">
                  <span
                    className={cn("h-1.5 w-1.5 shrink-0 rounded-full", accent.dot)}
                    aria-hidden
                  />
                  <span className="truncate">{item.label}</span>
                </span>
                {item.summary && (
                  <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-primary-foreground/85">
                    {item.summary}
                  </span>
                )}
                {item.grounded && item.groundLabel && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-primary-foreground/85">
                    <ShieldCheck className="h-3 w-3 text-success" aria-hidden />
                    {item.groundLabel}
                  </span>
                )}
              </div>
              {/* Provenance line, on its own row so its full length never crowds the
                  meta chips (owner 2026-07-22: "Generated by Atlas @ AISquare"). */}
              {item.subtitle && (
                <p className="mt-1.5 text-[11.5px] leading-snug text-primary-foreground/70">
                  {item.subtitle}
                </p>
              )}
              <h2
                className={cn(
                  "mt-2 break-words font-bold leading-tight tracking-[-0.01em] text-primary-foreground",
                  // Beside a full-height cover the naming column is narrower, so
                  // the title steps down a size and clamps at three lines — the
                  // full name lives in the preview body; the header stays a
                  // header, and the clamp is the other half of the media panel's
                  // max-h guard against tower heroes.
                  hasMedia ? "line-clamp-3 text-[17px]" : "text-[20px]",
                )}
                title={item.title}
              >
                {item.title}
              </h2>
              {/* PQA#2645 default path: tags sit with the identity, under the
                  title, outlined so they read as taxonomy distinct from the state
                  chips above. The opt-in #2615 layout moves them to the right-hand
                  `header-meta` column instead — see `hasMeta`. */}
              {!hasMeta && tags && (
                <ul className="m-0 mt-2 flex list-none flex-wrap items-center gap-1.5 p-0">
                  {tags.map((tag) => (
                    <li
                      key={tag}
                      className="rounded-full border border-white/25 px-2 py-[3px] text-[10.5px] font-medium text-primary-foreground/80"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {hasMeta && (
              <div
                data-slot="header-meta"
                className="ml-auto flex min-w-0 max-w-[60%] shrink-0 flex-col items-end gap-1.5"
              >
                {tags && (
                  <ul className="m-0 flex list-none flex-wrap items-center justify-end gap-1.5 p-0">
                    {tags.map((tag) => (
                      <li
                        key={tag}
                        className="rounded-full border border-white/25 px-2 py-[3px] text-[10.5px] font-medium text-primary-foreground/80"
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                )}
                {extra}
              </div>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-primary-foreground/70 transition-colors hover:bg-white/10 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Preview ---- */

function PreviewStandIn({ kind }: { kind: ArtifactFocusKind }) {
  // Lightweight per-kind placeholder for non-experience items (experiences get
  // the real v3 stage via the `preview` slot). Monochrome slate — supporting
  // kinds carry no identity hue.
  const bar = "rounded bg-muted";
  if (kind === "chart") {
    return (
      <div className="flex h-24 items-end gap-1.5" aria-hidden>
        {[40, 65, 30, 80, 55].map((h, i) => (
          <span
            key={i}
            className={cn("flex-1 rounded-t", i === 3 ? "bg-accent" : "bg-muted")}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    );
  }
  if (kind === "analysis") {
    return (
      <ul className="flex flex-col gap-2" aria-hidden>
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
            <span className={cn("h-2.5", bar)} style={{ width: `${85 - i * 18}%` }} />
          </li>
        ))}
      </ul>
    );
  }
  if (kind === "link") {
    return (
      <div className="flex items-center gap-2" aria-hidden>
        <span className="h-6 w-6 rounded bg-muted" />
        <span className={cn("h-2.5 w-1/2", bar)} />
      </div>
    );
  }
  // file (and the default): a page-thumb stack
  return (
    <div className="flex flex-col gap-1.5" aria-hidden>
      <span className={cn("h-2.5 w-1/3", bar)} />
      <span className={cn("h-2", bar)} />
      <span className={cn("h-2 w-5/6", bar)} />
      <span className={cn("h-2 w-2/3", bar)} />
    </div>
  );
}

function PreviewCell({
  item,
  preview,
  stageVariant,
}: {
  item: ArtifactFocusItem;
  preview?: React.ReactNode;
  /** "detached" never reaches here — the shell renders no preview row for it. */
  stageVariant: "carded" | "bare" | "detached" | "flush";
}) {
  const body = preview ?? <PreviewStandIn kind={item.kind} />;
  if (stageVariant === "bare") {
    // Un-carded, full-height stage: no border / background / label chrome. It is
    // the sole grower in the shell's flex column and OWNS its own scroll, so a
    // host can drop a full-height renderer (e.g. the v3 CenterStage) straight in
    // without the card clipping or collapsing it. The host supplies the height.
    return (
      <div data-slot="preview" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-auto">{body}</div>
      </div>
    );
  }
  if (stageVariant === "flush") {
    // Carded but LABELLESS and unpadded: the same card chrome as the default,
    // without the eyebrow band and without the cell's own padding, so the stage
    // meets the card edge and clips to its radius. This is the seam hosts were
    // hand-rolling — rendering `detached` and rebuilding a byte-matched card
    // around their own stage, plus a DOM-order bridge to lift the hero back
    // above it. It stays an ordinary row (NOT the sole grower `bare` creates),
    // so `belowPreview` and `actionsPlacement="belowHero"` still compose: a host
    // that wants a flush stage no longer has to choose between the eyebrow and
    // giving the preview the whole column height.
    return (
      <div
        data-slot="preview"
        className="col-span-2 overflow-hidden rounded-[13px] border border-border bg-card shadow-sm"
      >
        <div className="motion-safe:animate-card-in">{body}</div>
      </div>
    );
  }
  return (
    <div
      data-slot="preview"
      className="col-span-2 rounded-[13px] border border-border bg-card px-3.5 py-3 shadow-sm"
    >
      <span className={MONO_LABEL}>Preview</span>
      <div className="mt-2 motion-safe:animate-card-in">{body}</div>
    </div>
  );
}

/* ------------------------------------------------------- Details/Sources -- */

function DetailsCell({ facts }: { facts: ArtifactFocusItem["facts"] }) {
  return (
    <div className="rounded-[13px] border border-border bg-card px-3.5 py-3 shadow-sm">
      <span className={MONO_LABEL}>Details</span>
      <dl className="mt-2 flex flex-col gap-[7px]">
        {facts.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <dt className="font-mono text-[10px] text-muted-foreground">{k}</dt>
            <dd className="text-right text-[12px] font-medium text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Shared chip look; the interactive form adds affordance on top, never a new shape. */
const SOURCE_CHIP =
  "max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-[7px] border border-border bg-muted px-2 py-[3px] text-[10.5px] text-muted-foreground";

function SourcesCell({ sources }: { sources: ArtifactFocusItem["sources"] }) {
  return (
    <div className="rounded-[13px] border border-border bg-card px-3.5 py-3 shadow-sm">
      <span className={MONO_LABEL}>{sources.label}</span>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sources.chips.map((chip, i) => {
          const label = typeof chip === "string" ? chip : chip.label;
          const onClick = typeof chip === "string" ? undefined : chip.onClick;
          if (!onClick) {
            // The original static chip, byte-identical for string chips.
            return (
              <span key={`${label}-${i}`} title={label} className={SOURCE_CHIP}>
                {label}
              </span>
            );
          }
          return (
            <button
              key={`${label}-${i}`}
              type="button"
              title={label}
              aria-label={`Open source ${label}`}
              onClick={onClick}
              className={cn(
                SOURCE_CHIP,
                "cursor-pointer text-left transition-colors hover:border-ring/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- Actions ---- */

function ActionsRow({
  item,
  onAction,
}: {
  item: ArtifactFocusItem;
  onAction?: (a: string) => void;
}) {
  const actions = ACTIONS[item.kind];
  const primaryLabel = primaryActionLabel(item);
  return (
    <div data-slot="actions" className="col-span-2 flex shrink-0 gap-[7px]">
      {actions.map((action, i) => {
        const isPrimary = i === actions.length - 1;
        const label = isPrimary ? primaryLabel : action;
        return (
          <Button
            key={action}
            type="button"
            variant={isPrimary ? "default" : "outline"}
            // the intent stays the canonical action verb ("Open"); the label may
            // read "Open live ↗" / "Open draft" per status.
            onClick={() => onAction?.(action)}
            className="flex-1 rounded-[10px] text-[12px] font-semibold"
          >
            {label}
          </Button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------- Shell ----- */

export const ArtifactFocus = React.forwardRef<HTMLDivElement, ArtifactFocusProps>(
  (
    {
      item,
      preview,
      onAction,
      onClose,
      actions,
      stageVariant = "carded",
      headerMedia,
      headerExtra,
      actionsPlacement = "end",
      belowPreview,
      className,
      ...rest
    },
    ref,
  ) => {
    const bare = stageVariant === "bare";
    const detached = stageVariant === "detached";
    return (
      <div
        ref={ref}
        data-kind={item.kind}
        data-status={item.status}
        data-stage-variant={stageVariant}
        className={cn(
          // Default ("carded"): the original bento grid — every full-width row is
          // `col-span-2`, details + sources share one row. "flush" shares that
          // grid exactly and differs only INSIDE the preview cell (no eyebrow, no
          // padding) — that is the point: it stays a carded layout, so it
          // composes with every other row. "bare": a flex column
          // whose preview is the sole grower + scroll owner, so the component must
          // claim a height (`h-full min-h-0`) from the host. "detached": the same
          // flex column with no preview row and no height claim — chrome only,
          // sized to content (the host stacks the real stage as a sibling). The
          // `col-span-2` / `shrink-0` tokens on the rows are inert in whichever
          // layout ignores them, so the rows are shared across all variants.
          // Every direct child now carries `col-span-2`, so the carded grid's two
          // tracks are inert. Collapsing it changes the ROOT class string, which
          // is CONTRIBUTING's major-bump trigger, so that is a release decision.
          bare
            ? "flex h-full min-h-0 flex-col gap-[9px] motion-safe:animate-card-in"
            : detached
              ? "flex flex-col gap-[9px] motion-safe:animate-card-in"
              : "grid grid-cols-2 gap-[9px] motion-safe:animate-card-in",
          className,
        )}
        {...rest}
      >
        <HeroStage item={item} onClose={onClose} media={headerMedia} extra={headerExtra} />
        {/* PQA#2615 "belowHero": ONE compact status + actions row directly under
            the header, before the preview — the status flip stays beside the CTA
            that flips with it, and action is upfront. The default keeps the
            original end-of-card order untouched. */}
        {actionsPlacement === "belowHero" && (
          <div
            data-slot="actions-below-hero"
            className="col-span-2 flex shrink-0 items-center gap-2"
          >
            <StatusChip status={item.status} size="sm" />
            {actions !== undefined ? (
              <div data-slot="actions" className="min-w-0 flex-1">
                {actions}
              </div>
            ) : (
              <div className="min-w-0 flex-1">
                <ActionsRow item={item} onAction={onAction} />
              </div>
            )}
          </div>
        )}
        {!detached && <PreviewCell item={item} preview={preview} stageVariant={stageVariant} />}
        {/* PQA#2615: the host slot between the preview and the Details row — the
            editable tags block's home in the owner's layout. */}
        {belowPreview != null && (
          <div data-slot="below-preview" className="col-span-2 shrink-0">
            {belowPreview}
          </div>
        )}
        {/* Details + sources sit side by side when there is room and STACK when
            there is not. `auto-fit` rather than a breakpoint because this
            component's width is set by its host, not by the viewport: the studio
            context panel is resizable between 280px and 1020px, so it can be
            280px wide on a 2560px monitor and every `sm:`/`md:` prefix would read
            the wrong number. Measured before this: at the 280px floor the fixed
            two-track grid gave 108.5px columns and hid 185px of a source
            filename, which is the "nothing stacks however far I shrink it" report.

            `min(240px,100%)` not a bare `240px`: inside a container narrower than
            the track, a bare minimum still lays out at 240px and overflows. The
            `min()` clamps the track to the container on the single-column pass, so
            the floor degrades instead of spilling.

            One row for every variant now. Carded/flush previously leaned on the
            root grid's two tracks with these as plain children, which is why they
            could never stack either; the row carries `col-span-2` so it still
            spans that grid, and the two layouts stop diverging. */}
        <div
          data-slot="details-sources"
          className="col-span-2 grid shrink-0 grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-[9px]"
        >
          <DetailsCell facts={item.facts} />
          <SourcesCell sources={item.sources} />
        </div>
        {actionsPlacement === "end" && (
          <>
            {/* Status expression sits with the actions so the draft->published flip
                is visible next to the CTA that also flips. */}
            <div className="col-span-2 flex shrink-0 items-center justify-between gap-2">
              <StatusChip status={item.status} size="sm" />
            </div>
            {actions !== undefined ? (
              <div data-slot="actions" className="col-span-2 shrink-0">
                {actions}
              </div>
            ) : (
              <ActionsRow item={item} onAction={onAction} />
            )}
          </>
        )}
      </div>
    );
  },
);
ArtifactFocus.displayName = "ArtifactFocus";
